/**
 * EmailTemplatesPage - Manage email templates for financial documents
 * PROMPT 41: Financial Communications
 */
import { useState, useEffect, useCallback } from "react";
import {
  getEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  seedDefaultEmailTemplates,
  resetDefaultEmailTemplate,
  extractErrorMessage,
} from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Mail,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  FileText,
  Star,
  AlertCircle,
  Info,
} from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../components/ui/alert";

const TEMPLATE_TYPES = [
  { value: "quote", label: "Quote", description: "For sending quotes to clients" },
  { value: "invoice", label: "Invoice", description: "For sending invoices to clients" },
  { value: "reminder", label: "Payment Reminder", description: "For overdue payment reminders" },
];

const TEMPLATE_VARIABLES = [
  { name: "company_name", description: "Your company name" },
  { name: "client_name", description: "Client's name" },
  { name: "project_name", description: "Project name (auto-cleaned when missing)" },
  { name: "document_number", description: "Quote/Invoice number" },
  { name: "document_type", description: "Document type label (Quote / Invoice)" },
  // EVENTSFLO-QUOTE-INVOICE-EMAIL-TEMPLATE-SETTINGS-VISIBILITY-AND-PORTAL-COPY-FIX-V1
  { name: "client_portal_link", description: "Absolute link to the Client Portal (auto-cleaned when unavailable)" },
  { name: "total_amount", description: "Total amount" },
  { name: "currency_symbol", description: "Currency symbol (e.g., £, €)" },
  { name: "due_date", description: "Invoice due date" },
  { name: "valid_until", description: "Quote validity date" },
  { name: "payment_terms", description: "Payment terms text" },
  { name: "balance_due", description: "Outstanding balance" },
];

export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const [formData, setFormData] = useState({
    template_type: "quote",
    name: "",
    subject: "",
    body: "",
    is_default: false,
    is_active: true,
  });

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getEmailTemplates();
      setTemplates(response.data || []);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const openCreateDialog = () => {
    setEditingTemplate(null);
    setFormData({
      template_type: "quote",
      name: "",
      subject: "",
      body: "",
      is_default: false,
      is_active: true,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setFormData({
      template_type: template.template_type,
      name: template.name,
      subject: template.subject,
      body: template.body,
      is_default: template.is_default,
      is_active: template.is_active,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Template name is required");
      return;
    }
    if (!formData.subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    if (!formData.body.trim()) {
      toast.error("Body is required");
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await updateEmailTemplate(editingTemplate.id, formData);
        toast.success("Template updated successfully");
      } else {
        await createEmailTemplate(formData);
        toast.success("Template created successfully");
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return;

    try {
      await deleteEmailTemplate(template.id);
      toast.success("Template deleted");
      fetchTemplates();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await updateEmailTemplate(template.id, { is_active: !template.is_active });
      toast.success(template.is_active ? "Template deactivated" : "Template activated");
      fetchTemplates();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleSetDefault = async (template) => {
    try {
      await updateEmailTemplate(template.id, { is_default: true });
      toast.success(`"${template.name}" set as default`);
      fetchTemplates();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const response = await seedDefaultEmailTemplates();
      toast.success(response.data.message);
      fetchTemplates();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setSeeding(false);
    }
  };

  // EVENTSFLO-QUOTE-INVOICE-EMAIL-BODY-TEMPLATES-SETTINGS-V1
  // Reset the default template body+subject for `quote` or `invoice`
  // back to the platform-recommended concise text.
  const handleResetToDefault = async (templateType) => {
    const label = templateType === "quote" ? "Quote" : "Invoice";
    if (
      !window.confirm(
        `Reset the default ${label} email body and subject to the concise platform default?\n\nAny custom text on the current default template will be overwritten.`
      )
    ) {
      return;
    }
    try {
      await resetDefaultEmailTemplate(templateType);
      toast.success(`${label} email template reset to default`);
      fetchTemplates();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const getTemplateTypeLabel = (type) => {
    return TEMPLATE_TYPES.find((t) => t.value === type)?.label || type;
  };

  const groupedTemplates = TEMPLATE_TYPES.map((type) => ({
    ...type,
    templates: templates.filter((t) => t.template_type === type.value),
  }));

  return (
    <div className="space-y-6" data-testid="email-templates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Email Templates</h1>
          <p className="text-muted-foreground text-sm">
            Manage email templates for quotes, invoices, and reminders
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSeedDefaults} disabled={seeding}>
            {seeding ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 mr-2" />
            )}
            Seed Defaults          </Button>
          <Button onClick={openCreateDialog} data-testid="add-template-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Template
          </Button>
        </div>
      </div>

      {/* Variable Reference */}
      <Alert data-testid="email-templates-helper-text">
        <Info className="h-4 w-4" />
        <AlertTitle>Quote &amp; Invoice email templates</AlertTitle>
        <AlertDescription>
          <p className="mb-2 text-sm">
            Used when sending Quotes and Invoices by email. The PDF is
            attached automatically. Use the <code className="font-mono text-xs">{`{{client_portal_link}}`}</code>
            {" "}placeholder to direct clients to their portal.
          </p>
          <p className="mb-2 text-xs text-muted-foreground">
            Available variables (wrap in double curly braces):
          </p>
          <div className="flex flex-wrap gap-2">
            {TEMPLATE_VARIABLES.map((v) => (
              <Badge
                key={v.name}
                variant="outline"
                className="font-mono text-xs"
                title={v.description}
              >
                {`{{${v.name}}}`}
              </Badge>
            ))}
          </div>
        </AlertDescription>
      </Alert>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : templates.length === 0 ? (
        /* Empty State */
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Mail className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No templates configured</h3>
            <p className="text-muted-foreground text-sm mb-4 text-center max-w-md">
              Create email templates for quotes and invoices, or click &quot;Seed Defaults&quot; to create standard templates.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleSeedDefaults} disabled={seeding}>
                Seed Defaults
              </Button>
              <Button onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Template Groups */
        <div className="space-y-6">
          {groupedTemplates.map((group) => (
            <Card key={group.value}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Mail className="h-5 w-5" />
                      {group.label} Templates
                    </CardTitle>
                    <CardDescription>{group.description}</CardDescription>
                  </div>
                  {/* EVENTSFLO-QUOTE-INVOICE-EMAIL-BODY-TEMPLATES-SETTINGS-V1 */}
                  {(group.value === "quote" || group.value === "invoice") && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleResetToDefault(group.value)}
                      data-testid={`reset-${group.value}-template-btn`}
                    >
                      Reset to default
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {group.templates.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    No templates for this type
                  </p>
                ) : (
                  <div className="space-y-3">
                    {group.templates.map((template) => (
                      <div
                        key={template.id}
                        className="flex items-start justify-between p-4 bg-surface-elevated/50 rounded-lg border border-border"
                        data-testid={`template-${template.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-white">
                              {template.name}
                            </span>
                            {template.is_default && (
                              <Badge className="bg-teal-900 text-teal-200 border-teal-700">
                                <Star className="h-3 w-3 mr-1" />
                                Default
                              </Badge>
                            )}
                            {!template.is_active && (
                              <Badge variant="outline" className="text-yellow-500 border-yellow-500">
                                Inactive
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2 truncate">
                            Subject: {template.subject}
                          </p>
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {template.body}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Switch
                            checked={template.is_active}
                            onCheckedChange={() => handleToggleActive(template)}
                            className="data-[state=checked]:bg-teal-600"
                          />
                          {!template.is_default && template.is_active && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSetDefault(template)}
                              className="text-muted-foreground hover:text-teal-400"
                              title="Set as default"
                            >
                              <Star className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(template)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(template)}
                            className="text-muted-foreground hover:text-red-400"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Edit Template" : "Create Template"}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate
                ? "Update the email template settings"
                : "Create a new email template for financial documents"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Template Type */}
            {!editingTemplate && (
              <div className="space-y-2">
                <Label>Template Type</Label>
                <Select
                  value={formData.template_type}
                  onValueChange={(value) =>
                    setFormData({ ...formData, template_type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Name */}
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., Standard Quote Email"
              />
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={formData.subject}
                onChange={(e) =>
                  setFormData({ ...formData, subject: e.target.value })
                }
                placeholder="e.g., Quote {{document_number}} from {{company_name}}"
                data-testid={
                  formData.template_type === "quote"
                    ? "quote-email-template-subject"
                    : formData.template_type === "invoice"
                    ? "invoice-email-template-subject"
                    : "email-template-subject"
                }
              />
              <p className="text-xs text-muted-foreground">
                Use variables like {`{{document_number}}`} for dynamic content
              </p>
            </div>

            {/* Body */}
            <div className="space-y-2">
              <Label>Email Body</Label>
              <Textarea
                value={formData.body}
                onChange={(e) =>
                  setFormData({ ...formData, body: e.target.value })
                }
                placeholder="Dear {{client_name}},&#10;&#10;Please find attached..."
                rows={10}
                className="font-mono text-sm"
                data-testid={
                  formData.template_type === "quote"
                    ? "quote-email-template-body"
                    : formData.template_type === "invoice"
                    ? "invoice-email-template-body"
                    : "email-template-body"
                }
              />
            </div>

            {/* Options */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_default}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_default: checked })
                  }
                />
                <Label>Set as default</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />
                <Label>Active</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-email-templates-btn">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Template"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
